/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControlLabel, Switch, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { StepperContentProps } from '.';
import { Autocomplete, Input } from '../../../../../../components';
import { minutesToMicrosseconds } from '../../../../../../utils/time';
import { ServiceConfigurationsData, configurationsDataSchema } from '../../../../../../validations/service.validation';
import useStyles from './styles';
import ReservationScope from '../../../../../../entities/reservation-scope.type';

const durationOptions: Record<string, string> = {
  [minutesToMicrosseconds(15).toString()]: '15 minutos',
  [minutesToMicrosseconds(30).toString()]: '30 minutos',
  [minutesToMicrosseconds(45).toString()]: '45 minutos',
  [minutesToMicrosseconds(60).toString()]: '1 hora',
  [minutesToMicrosseconds(120).toString()]: '2 horas',
  [minutesToMicrosseconds(180).toString()]: '3 horas',
  [minutesToMicrosseconds(240).toString()]: '4 horas',
  [minutesToMicrosseconds(360).toString()]: '6 horas',
  [minutesToMicrosseconds(480).toString()]: '8 horas',
  [minutesToMicrosseconds(720).toString()]: '12 horas',
  [minutesToMicrosseconds(1440).toString()]: '24 horas',
};

const reservationScopeLabel = {
  [ReservationScope.Dweller]: 'Morador',
  [ReservationScope.Unit]: 'Unidade'
};

type ConfigurationProps = Omit<StepperContentProps, 'activeStep' | 'handleUpload' | 'file'>;

export const Configurations = ({
  service,
  shouldValidate,
  setShouldValidate,
  handleSetSelectedService,
  validate,
}: ConfigurationProps): JSX.Element => {
  const styles = useStyles();
  const { palette } = useTheme();
  const {
    formState: { errors },
    control,
    register,
    reset,
    getValues,
    setValue,
    handleSubmit,
  } = useForm<ServiceConfigurationsData>({
    resolver: yupResolver(configurationsDataSchema),
    mode: 'onChange',
  });

  const reservationDuration = useWatch({ control, name: 'reservationDuration' });
  const reservationScope = useWatch({ control, name:  'reservationScope' });
  const disableSequentialReservations = useWatch({ control, name: 'disableSequentialReservations' });
  const enableExclusiveReservations = useWatch({ control, name: 'enableExclusiveReservations' });
  useEffect(() => {
    reset({
      maxReservationsByUnit: service.maxReservationsByUnit?.toString() || '',
      maxReservationsByDweller: service.maxReservationsByDweller?.toString() || '',
      maxSimultaneosReservations: service.maxSimultaneosReservations?.toString() || '',
      reservationDuration: service.reservationDuration ? service.reservationDuration.toString() : undefined,
      reservationScope: service.reservationScope as ReservationScope,
      disableSequentialReservations: service.disableSequentialReservations || false,
      enableExclusiveReservations: service.enableExclusiveReservations || false,
    });
  }, [service, reset]);

  useEffect(() => {
    if (shouldValidate) {
      const handle = handleSubmit(() => {
        const {
          disableSequentialReservations: disableSequentialReservationsInput,
          maxReservationsByUnit,
          maxReservationsByDweller,
          maxSimultaneosReservations,
          reservationDuration: reservationDurantionInput,
          enableExclusiveReservations: enableExclusiveReservationsInput,
          reservationScope: reservationScopeInput,
        } = getValues();

        handleSetSelectedService({
          maxReservationsByUnit: maxReservationsByUnit ? Number(maxReservationsByUnit) : undefined,
          maxReservationsByDweller: maxReservationsByDweller ? Number(maxReservationsByDweller) : undefined,
          maxSimultaneosReservations: maxSimultaneosReservations ? Number(maxSimultaneosReservations) : undefined,
          reservationDuration: Number(reservationDurantionInput),
          disableSequentialReservations: disableSequentialReservationsInput,
          enableExclusiveReservations: enableExclusiveReservationsInput,
          reservationScope: reservationScopeInput,
        });
        validate(true);
      });

      handle().then(() => {
        setShouldValidate(false);
      });
    }
  }, [shouldValidate, setShouldValidate, validate, handleSetSelectedService, getValues, handleSubmit]);

  return (
    <div className={styles.container}>
      <Input
        label="reservas simultâneas"
        mask={{ mask: '0000' }}
        placeholder="Ex: 05"
        validationRegister={register('maxSimultaneosReservations')}
        error={Boolean(errors.maxSimultaneosReservations)}
        helperText={errors.maxSimultaneosReservations?.message || ' '}
      />

      <Autocomplete
        label="selecione o tipo de reserva"
        placeholder="Ex: por morador"
        options={Object.keys(reservationScopeLabel)}
        getOptionLabel={(value) => reservationScopeLabel[value as ReservationScope]}
        value={reservationScope || null}
        error={Boolean(errors.reservationScope)}
        helperText={errors.reservationScope?.message || ' '}
        onChange={(_, value) => {
          setValue('reservationScope', value as ReservationScope);
        }}
      />

    {reservationScope === ReservationScope.Unit ? (
      <Input
        label="quantidade de reservas por unidade"
        placeholder="Ex: 05"
        validationRegister={register('maxReservationsByUnit')}
        mask={{ mask: '0000' }}
        error={Boolean(errors.maxReservationsByUnit)}
        helperText={errors.maxReservationsByUnit?.message || ' '}
      />
    ) : (
      <Input
        label="quantidade de reservas por morador"
        placeholder="Ex: 05"
        validationRegister={register('maxReservationsByDweller')}
        mask={{ mask: '0000' }}
        error={Boolean(errors.maxReservationsByDweller)}
        helperText={errors.maxReservationsByDweller?.message || ' '}
      />
    )}



      <Autocomplete
        label="duração da reserva"
        placeholder="Ex: 1 hora"
        options={Object.keys(durationOptions)}
        value={reservationDuration || null}
        onChange={(_, value) => {
          setValue('reservationDuration', value as string);
        }}
        noOptionsText="Nada aqui..."
        getOptionLabel={(opt) => durationOptions[opt]}
        error={Boolean(errors.reservationDuration)}
        helperText={errors.reservationDuration?.message || ' '}
      />
      <FormControlLabel
        label="Desabilitar reservas seguidas"
        style={{ marginLeft: '1px' }}
        sx={{ color: palette.grey[100] }}
        control={
          <Switch
            value={disableSequentialReservations || false}
            checked={disableSequentialReservations || false}
            onChange={(_, checked) => setValue('disableSequentialReservations', !disableSequentialReservations)}
          />
        }
      />
      <FormControlLabel
        label="Habilitar reservas exclusivas"
        style={{ marginLeft: '1px' }}
        sx={{ color: palette.grey[100] }}
        control={
          <Switch
            value={enableExclusiveReservations || false}
            checked={enableExclusiveReservations || false}
            onChange={(_, checked) => setValue('enableExclusiveReservations', !enableExclusiveReservations)}
          />
        }
      />
    </div>
  );
};

export default Configurations;
